import { useState, useCallback, useRef } from 'preact/hooks';
import { landingPagesApi } from '@/api/landingPages';
import { useToast } from '@/hooks/useToast';
export function useLandingPage({ clientCompanyId, workspaceId, campaignId, webId }) {
    const { showError, showSuccess } = useToast();
    const [landingPage, setLandingPage] = useState(null);
    const [html, setHtml] = useState(null);
    const [thankYouHtml, setThankYouHtml] = useState(null);
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(false);
    const [contextLoading, setContextLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const hasLoadedOnce = useRef(false);
    const load = useCallback(async () => {
        // Only show loading spinner on initial load, not on reloads after generation
        if (!hasLoadedOnce.current)
            setLoading(true);
        setError(null);
        const res = await landingPagesApi.getLandingPage(workspaceId, webId);
        if (res.data) {
            setLandingPage(res.data);
        }
        else if (res.status !== 404) {
            const message = res.error || 'Failed to load landing page';
            setError(message);
            showError('Load failed', message);
        }
        if (!hasLoadedOnce.current) {
            hasLoadedOnce.current = true;
            setLoading(false);
        }
        return res.data ?? null;
    }, [workspaceId, webId]);
    const loadDraft = useCallback(async () => {
        const res = await landingPagesApi.getLandingPageDraft(workspaceId, webId);
        if (res.data) {
            setHtml(res.data);
        }
        else if (res.status !== 404 && res.error) {
            showError('Failed to load preview', res.error);
        }
    }, [workspaceId, webId]);
    const loadContext = useCallback(async () => {
        setContextLoading(true);
        const res = await landingPagesApi.getContext(clientCompanyId, workspaceId, campaignId, webId);
        if (res.data) {
            setContext(res.data);
        }
        else if (res.error) {
            console.warn('Failed to load context:', res.error);
        }
        setContextLoading(false);
    }, [clientCompanyId, workspaceId, campaignId, webId]);
    const updateContext = useCallback(async (dto) => {
        const res = await landingPagesApi.updateContext(workspaceId, webId, dto);
        if (res.data) {
            setLandingPage(res.data);
            if (res.data.Context) {
                setContext(res.data.Context);
            }
        }
        else {
            showError('Update failed', res.error || 'Unable to update context');
        }
    }, [workspaceId, webId]);
    const createAndGenerate = useCallback(async (request) => {
        setGenerating(true);
        setHtml(null);
        setThankYouHtml(null);
        setError(null);
        const createRes = await landingPagesApi.createLandingPage(clientCompanyId, workspaceId, campaignId, webId, request);
        if (createRes.error) {
            setGenerating(false);
            showError('Create failed', createRes.error);
            return;
        }
        if (createRes.data) {
            setLandingPage(createRes.data);
            if (createRes.data.Context) {
                setContext(createRes.data.Context);
            }
            // Don't load HTML here — generation is async (~2 min).
            // SignalR onStreamResult will load HTML when the AI finishes.
        }
        setGenerating(false);
    }, [clientCompanyId, workspaceId, campaignId, webId]);
    /** Fire-and-forget for 202-Accepted endpoints where SignalR drives state updates. */
    const check202 = useCallback((res, errorTitle) => {
        if (res.error && res.status !== 202) {
            showError(errorTitle, res.error || 'Request failed');
        }
    }, []);
    const regenerate = useCallback(async (prompt) => {
        setGenerating(true);
        setHtml(null);
        const res = await landingPagesApi.generateLandingPage(workspaceId, webId, prompt);
        check202(res, 'Update failed');
        setGenerating(false);
    }, [workspaceId, webId, check202]);
    const updateSettings = useCallback(async (dto) => {
        const res = await landingPagesApi.updateSettings(workspaceId, webId, dto);
        if (res.data) {
            setLandingPage(res.data);
            showSuccess('Settings updated', 'Landing page settings have been saved.');
        }
        else {
            showError('Update failed', res.error || 'Unable to update settings');
        }
    }, [workspaceId, webId]);
    const loadThankYouDraft = useCallback(async () => {
        const res = await landingPagesApi.getThankYouPageDraft(workspaceId, webId);
        if (res.data) {
            setThankYouHtml(res.data);
        }
        else if (res.status !== 404 && res.error) {
            showError('Failed to load thank you page preview', res.error);
        }
    }, [workspaceId, webId]);
    const updateThankYouSettings = useCallback(async (dto) => {
        const res = await landingPagesApi.updateThankYouSettings(workspaceId, webId, dto);
        if (res.data) {
            setLandingPage(res.data);
            showSuccess('Settings updated', 'Thank you page settings have been saved.');
        }
        else {
            showError('Update failed', res.error || 'Unable to update thank you settings');
        }
    }, [workspaceId, webId]);
    const updatePageMeta = useCallback(async (dto) => {
        const res = await landingPagesApi.updatePageMeta(workspaceId, webId, dto);
        if (res.data) {
            setLandingPage(res.data);
        }
        else {
            showError('Update failed', res.error || 'Unable to update page meta');
        }
    }, [workspaceId, webId]);
    const regenerateThankYou = useCallback(async (prompt) => {
        const res = await landingPagesApi.regenerateThankYou(workspaceId, webId, prompt);
        check202(res, 'Generate failed');
    }, [workspaceId, webId, check202]);
    const setMode = useCallback(async (mode, externalUrl) => {
        const res = await landingPagesApi.setMode(workspaceId, campaignId, webId, {
            ETag: landingPage?.ETag,
            Mode: mode,
            ExternalUrl: externalUrl,
        });
        if (res.data) {
            setLandingPage(res.data);
            showSuccess(mode === 'External' ? 'External URL set' : 'Switched to Builder', mode === 'External'
                ? 'Landing page now uses your custom URL.'
                : 'Landing page switched back to the AI builder.');
            return res.data;
        }
        showError('Update failed', res.error || 'Unable to update landing page mode');
        return null;
    }, [workspaceId, campaignId, webId, landingPage?.ETag]);
    const deleteLandingPage = useCallback(async () => {
        const res = await landingPagesApi.deleteLandingPage(workspaceId, webId);
        if (res.status === 204 || res.status === 200) {
            setLandingPage(null);
            setHtml(null);
            setThankYouHtml(null);
            setContext(null);
            showSuccess('Deleted', 'Landing page and all versions have been removed.');
        }
        else {
            showError('Delete failed', res.error || 'Unable to delete landing page');
        }
    }, [workspaceId, webId]);
    return {
        landingPage,
        setLandingPage,
        html,
        context,
        loading,
        contextLoading,
        generating,
        error,
        load,
        loadDraft,
        loadContext,
        updateContext,
        createAndGenerate,
        regenerate,
        thankYouHtml,
        loadThankYouDraft,
        updateThankYouSettings,
        updatePageMeta,
        regenerateThankYou,
        updateSettings,
        deleteLandingPage,
        setMode,
    };
}
